import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, Building2, ChevronLeft, ChevronRight, MessageCircle, X } from 'lucide-react';
import { useAuth } from '../auth';
import { Button, ModalOverlay } from './ui';
import {
  approvedWelcomeShouldShow,
  markApprovedWelcomeSeen,
  noteStudentPending,
  setApprovedWelcomeDialogOpen,
} from '../lib/studentWelcome';

const SLIDES = [
  {
    icon: Building2,
    title: 'Clinic weeks',
    body: 'Pick a partner clinic and book a week. That’s the internship — real patients, real hours.',
  },
  {
    icon: MessageCircle,
    title: 'Mentor questions',
    body: 'Stuck on a case? Send it to a doctor on PhysioEDGE and get an answer you can use.',
  },
  {
    icon: Award,
    title: 'Scores and certificates',
    body: 'The clinic scores your week. Do well and you get a certificate you can share.',
  },
];

export function AccountApprovedWelcomeHost() {
  const { me, loading } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (me?.id && me.role === 'student' && me.approvalStatus === 'pending') {
      noteStudentPending(me.id);
    }
  }, [me?.id, me?.role, me?.approvalStatus]);

  useEffect(() => {
    if (loading) return;
    const show = approvedWelcomeShouldShow(me);
    setOpen(show);
    setApprovedWelcomeDialogOpen(show);
  }, [loading, me]);

  if (!open || !me?.id) return null;

  const finish = () => {
    markApprovedWelcomeSeen(me.id);
    setOpen(false);
    setApprovedWelcomeDialogOpen(false);
  };

  return <AccountApprovedWelcomeDialog onClose={finish} />;
}

function AccountApprovedWelcomeDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const scroller = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);

  const onScroll = () => {
    const el = scroller.current;
    if (!el || el.clientWidth === 0) return;
    setPage(Math.round(el.scrollLeft / el.clientWidth));
  };

  const goTo = (i: number) => {
    const el = scroller.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  };

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <ModalOverlay>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="approved-welcome-title"
        className="app-dialog relative bg-surface shadow-pop w-full max-w-md overflow-hidden"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 flex h-11 w-11 items-center justify-center rounded-full text-white hover:bg-white/20 touch-manipulation"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="bg-teal px-6 pt-6 pb-5 text-white pr-14">
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/75 mb-2">
            You’re in
          </p>
          <h2 id="approved-welcome-title" className="font-heading text-xl sm:text-2xl font-bold leading-tight text-sand">
            Your account is approved
          </h2>
          <p className="text-sm text-white/90 mt-2">
            Internships, mentor questions, and certificates are open for you. Use the arrows to see how it works.
          </p>
        </div>

        <div className="p-5 sm:p-6">
          <div className="relative">
            <div
              ref={scroller}
              onScroll={onScroll}
              className="flex overflow-x-auto snap-x snap-mandatory scrollbar-none px-8"
              style={{ scrollbarWidth: 'none' }}
            >
              {SLIDES.map(slide => {
                const Icon = slide.icon;
                return (
                  <div
                    key={slide.title}
                    className="min-w-full snap-center px-1"
                  >
                    <div className="rounded-xl border border-line bg-bg px-4 py-4 min-h-[8.5rem]">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-teal-soft text-teal mb-3">
                        <Icon className="w-4 h-4" aria-hidden />
                      </span>
                      <div className="font-heading font-bold text-ink">{slide.title}</div>
                      <p className="text-sm text-ink-soft mt-1">{slide.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              aria-label="Previous"
              disabled={page <= 0}
              onClick={() => goTo(page - 1)}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-surface border border-line text-ink shadow-sm touch-manipulation disabled:opacity-30"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              aria-label="Next"
              disabled={page >= SLIDES.length - 1}
              onClick={() => goTo(page + 1)}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-surface border border-line text-ink shadow-sm touch-manipulation disabled:opacity-30"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="flex justify-center gap-2 mt-3 mb-5" role="tablist" aria-label="How PhysioEDGE works">
            {SLIDES.map((slide, i) => (
              <button
                key={slide.title}
                type="button"
                role="tab"
                aria-selected={page === i}
                aria-label={slide.title}
                className={`h-2 rounded-full transition-all ${page === i ? 'w-6 bg-teal' : 'w-2 bg-line'}`}
                onClick={() => goTo(i)}
              />
            ))}
          </div>

          <div className="flex flex-col gap-2">
            <Button className="w-full" size="lg" onClick={() => go('/clinics')}>
              Explore clinics
            </Button>
            <Button className="w-full" variant="secondary" size="lg" onClick={() => go('/doctors')}>
              Explore doctors
            </Button>
          </div>
        </div>
      </div>
    </ModalOverlay>
  );
}
